
import { useState } from "react";
import Hero from "@/components/Hero";
import EpisodeCard from "@/components/EpisodeCard";
import VideoEmbed from "@/components/VideoEmbed";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Users, Globe } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useFeaturedEpisodes } from "@/hooks/useFeaturedEpisodes";

const Index = () => {
  const [selectedVideo, setSelectedVideo] = useState<{
    youtubeId?: string;
    vimeoId?: string;
    videoPlatform?: "youtube" | "vimeo";
  } | null>(null);
  const { episodes: featuredEpisodes, loading } = useFeaturedEpisodes();

  const handleVideoPlay = (youtubeId?: string, vimeoId?: string, videoPlatform?: "youtube" | "vimeo") => {
    setSelectedVideo({ youtubeId, vimeoId, videoPlatform });
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section */}
        <Hero />

        {/* Features Section */}
        <section className="w-full py-24 lg:py-32 bg-white/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl lg:text-5xl font-bold text-teedee-blue mb-6">
                Why Kids Love Bobo & Teedee®
              </h2>
              <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Educational entertainment that combines fun storytelling with valuable life lessons and Cayman Islands culture.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="text-center p-10 bg-white/70 rounded-3xl shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="w-20 h-20 bg-teedee-blue/20 rounded-full flex items-center justify-center mb-8 mx-auto">
                  <Star className="h-10 w-10 text-teedee-blue" />
                </div>
                <h3 className="text-2xl font-bold text-teedee-blue mb-6">Educational Fun</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Learning through play with engaging stories that teach valuable lessons about friendship, kindness, and cultural appreciation.
                </p>
              </div>

              <div className="text-center p-10 bg-white/70 rounded-3xl shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="w-20 h-20 bg-bobo-green/20 rounded-full flex items-center justify-center mb-8 mx-auto">
                  <Globe className="h-10 w-10 text-bobo-green" />
                </div>
                <h3 className="text-2xl font-bold text-bobo-green mb-6">Cayman Culture</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Authentic representation of Cayman Islands culture, traditions, and values, helping children connect with their heritage.
                </p>
              </div>

              <div className="text-center p-10 bg-white/70 rounded-3xl shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <div className="w-20 h-20 bg-brand-purple/20 rounded-full flex items-center justify-center mb-8 mx-auto">
                  <Users className="h-10 w-10 text-brand-purple" />
                </div>
                <h3 className="text-2xl font-bold text-brand-purple mb-6">Family Friendly</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Content designed for children but enjoyable for the whole family, promoting quality time and shared learning experiences.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Episodes */}
        <section className="w-full py-24 lg:py-32 bg-gradient-to-br from-sky-blue/20 to-bobo-green/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-20">
              <h2 className="text-4xl lg:text-5xl font-bold text-teedee-blue mb-6">
                Featured Episodes
              </h2>
              <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Dive into our most popular adventures with Bobo & Teedee®!
              </p>
            </div>

            {loading ? (
              <div className="text-center">
                <p className="text-xl text-gray-600">Loading featured episodes...</p>
              </div>
            ) : featuredEpisodes.length === 0 ? (
              <div className="text-center">
                <p className="text-xl text-gray-600">No featured episodes available at the moment.</p>
                <p className="text-lg text-gray-500 mt-2">Check back soon for exciting new content!</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
                  {featuredEpisodes.map((episode) => (
                    <EpisodeCard
                      key={episode.id}
                      title={episode.title}
                      description={episode.description || ""}
                      thumbnail={episode.thumbnail_url || ""}
                      duration={episode.duration || "0:00"}
                      youtubeId={episode.youtube_id || undefined}
                      vimeoId={episode.vimeo_id || undefined}
                      videoPlatform={episode.video_platform || undefined}
                      onPlay={handleVideoPlay}
                    />
                  ))}
                </div>

                <div className="text-center">
                  <Button asChild size="lg" className="bg-bobo-green hover:bg-bobo-green/90 text-white text-xl px-10 py-6 rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
                    <Link to="/episodes">
                      View All Episodes <ArrowRight className="ml-3 h-6 w-6" />
                    </Link>
                  </Button>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="w-full bg-gradient-to-r from-teedee-blue via-bobo-green to-brand-purple text-white">
          <div className="w-full py-24 lg:py-32">
            <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
              <h2 className="text-4xl lg:text-5xl font-bold mb-8">
                Join the Adventure Today!
              </h2>
              <p className="text-xl lg:text-2xl mb-12 opacity-90 leading-relaxed">
                Subscribe to never miss new episodes and educational activities with Bobo & Teedee®.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a 
                  href="/contact" 
                  className="bg-white text-teedee-blue hover:bg-white/90 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 inline-block"
                >
                  Subscribe Now
                </a>
                <a 
                  href="/activities" 
                  className="border-2 border-white text-white hover:bg-white hover:text-teedee-blue px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 inline-block"
                >
                  Explore Activities
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        <VideoEmbed
          isOpen={!!selectedVideo}
          onClose={() => setSelectedVideo(null)}
          youtubeId={selectedVideo?.youtubeId}
          vimeoId={selectedVideo?.vimeoId}
          videoPlatform={selectedVideo?.videoPlatform}
        />
      </div>
      <Footer />
    </>
  );
};

export default Index;
